import { useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react';
import type { PaneKey, PaneTabs } from './tabs.ts';

const MIME = 'application/x-prism-tab';

/**
 * 탭 바 (CR-011 · CR-014): 선택·닫기·새 탭·드래그로 순서 변경·다른 화면으로 이동(드래그 또는 ⇄ 버튼).
 * 넘침(CR-014): 탭은 최소 폭까지 줄어들고, 그래도 넘치면 스크롤바 없는 스트립을 ‹ ›·휠·활성 탭 자동 노출로 넘긴다.
 * +·다른 탭 닫기는 스트립 밖 오른쪽에 고정.
 */
export function TabsBar({ pane, state, onSelect, onClose, onNew, onMove, canMove, otherLabel, onCloseOthers }: {
  pane: PaneKey; state: PaneTabs; onSelect: (id: string) => void; onClose: (id: string) => void; onNew: () => void;
  onMove: (from: PaneKey, id: string, to: PaneKey, index?: number) => void; canMove: boolean; otherLabel: string; onCloseOthers?: () => void;
}) {
  const [over, setOver] = useState<number | null>(null);
  const [scroll, setScroll] = useState({ overflow: false, atStart: true, atEnd: true });
  const strip = useRef<HTMLDivElement>(null);
  const other: PaneKey = pane === 'left' ? 'right' : 'left';
  const parse = (e: DragEvent) => { try { return JSON.parse(e.dataTransfer.getData(MIME)) as { pane: PaneKey; id: string }; } catch { return null; } };

  // 넘침·끝 도달 상태: 탭 수·폭·스크롤 위치가 바뀔 때 갱신
  const measure = () => {
    const el = strip.current; if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setScroll((s) => (s.overflow === overflow && s.atStart === atStart && s.atEnd === atEnd ? s : { overflow, atStart, atEnd }));
  };
  useLayoutEffect(measure, [state.tabs.length]);
  useEffect(() => {
    const el = strip.current; if (!el) return;
    const ro = new ResizeObserver(measure); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // 활성 탭이 바뀌거나 탭이 추가되면 활성 탭을 보이는 위치로 (‹ › 가 나타나 스트립이 좁아진 뒤에도 한 번 더)
  useEffect(() => {
    strip.current?.querySelector<HTMLElement>('.tab-active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    measure();
  }, [state.active, state.tabs.length, scroll.overflow]);
  // 세로 휠 → 가로 스크롤 (트랙패드 가로 스와이프는 그대로)
  useEffect(() => {
    const el = strip.current; if (!el) return;
    const h = (e: WheelEvent) => { if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && el.scrollWidth > el.clientWidth) { e.preventDefault(); el.scrollLeft += e.deltaY; } };
    el.addEventListener('wheel', h, { passive: false });
    return () => el.removeEventListener('wheel', h);
  }, []);
  const scrollBy = (dir: -1 | 1) => { const el = strip.current; if (el) el.scrollBy({ left: dir * el.clientWidth * 0.6, behavior: 'smooth' }); };

  return (
    <div className="tabbar">
      {scroll.overflow && <button type="button" className="tab-scroll" title="이전 탭" disabled={scroll.atStart} onClick={() => scrollBy(-1)}>‹</button>}
      <div ref={strip} className="tabstrip" onScroll={measure}
        onDragOver={(e) => { if (e.dataTransfer.types.includes(MIME)) { e.preventDefault(); setOver(state.tabs.length); } }} onDragLeave={() => setOver(null)}
        onDrop={(e) => { const d = parse(e); setOver(null); if (d) { e.preventDefault(); onMove(d.pane, d.id, pane, state.tabs.length); } }}>
        {state.tabs.map((t, i) => (
          <div key={t.id} className={`tab ${t.id === state.active ? 'tab-active' : ''} ${over === i ? 'tab-over' : ''}`} draggable title={t.title ? `${t.title} · ${t.path}` : t.path}
            onClick={() => onSelect(t.id)} onAuxClick={(e) => { if (e.button === 1) onClose(t.id); }}
            onDragStart={(e) => { e.dataTransfer.setData(MIME, JSON.stringify({ pane, id: t.id })); e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { if (e.dataTransfer.types.includes(MIME)) { e.preventDefault(); e.stopPropagation(); setOver(i); } }}
            onDrop={(e) => { const d = parse(e); setOver(null); if (d) { e.preventDefault(); e.stopPropagation(); onMove(d.pane, d.id, pane, i); } }}>
            <span className="tab-title">{t.title}</span>
            {canMove && (t.id === state.active ? <button type="button" className="tab-btn" title={`${otherLabel} 화면으로 이동`} onClick={(e) => { e.stopPropagation(); onMove(pane, t.id, other); }}>⇄</button> : <span className="tab-btn tab-btn-ghost" aria-hidden />)}
            <button type="button" className="tab-btn" title="닫기" onClick={(e) => { e.stopPropagation(); onClose(t.id); }}>×</button>
          </div>
        ))}
      </div>
      {scroll.overflow && <button type="button" className="tab-scroll" title="다음 탭" disabled={scroll.atEnd} onClick={() => scrollBy(1)}>›</button>}
      <button type="button" className="tab-new" title="새 탭" onClick={onNew}>+</button>
      {state.tabs.length > 1 && onCloseOthers && <button type="button" className="tab-new tab-close-others" title="활성 탭만 남기고 모두 닫기" onClick={onCloseOthers}>다른 탭 닫기</button>}
    </div>
  );
}
